/**
 * Pure helpers extracted from `useForm/index.tsx`'s `sendDataToServer`.
 *
 * These have no React or runtime-ref dependencies. Keeping them out of
 * the hook file makes the orchestrator readable and gives the
 * data-shaping logic clear inputs/outputs.
 */

import { base64DecodeUnicode, base64EncodeUnicode } from "@lib/sharedUtils";
import { gettingQueriesAsString } from "@lib/shared/routerQueriesToString";
import { Field } from "@lib/shared/types";
import { DomainForm } from "src/api";
import { FieldsData } from "../types";

const LEAD_CAPTURE_ALLOWED_FIELDS = [
    "checkbox",
    "radio",
    "select",
    "multiSelect",
];

const CONTACT_US_MAIN_FIELDS = [
    "email",
    "firstName",
    "lastName",
    "phoneNumber",
];

export interface FieldWithValue {
    codeName: string;
    value: string;
}

export interface FilledDefaultsResult {
    fields: FieldWithValue[];
    autoFilledFieldCodeNames: string[];
    selectedOptions: Record<string, number>;
}

/**
 * Collects only fields with non-empty values, applying the lead-capture
 * label substitution for select-like fields when relevant.
 */
export interface CollectFieldsResult {
    fields: FieldWithValue[];
    selectedOptions: Record<string, number>;
}

const parseSelectedOptionIndex = (
    optionSelectedIndex?: string,
): number | undefined => {
    if (!optionSelectedIndex) return undefined;
    const indexPart = optionSelectedIndex.split("-")[1];
    const parsed = Number.parseInt(indexPart, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
};

export const collectFieldsWithValues = (
    newFieldsState: FieldsData,
    form: DomainForm,
    opts: { isSecondServiceForm: boolean; leadCaptureForm: boolean },
): CollectFieldsResult => {
    const selectedOptions: Record<string, number> = {};

    if (opts.isSecondServiceForm) {
        const fields = form.steps[0].fields
            .filter((f) => newFieldsState[f.codeName]?.value?.trim())
            .map((f) => {
                const fieldState = newFieldsState[f.codeName];
                const selectedIndex = parseSelectedOptionIndex(
                    fieldState?.optionSelectedIndex,
                );
                if (selectedIndex !== undefined) {
                    selectedOptions[f.codeName] = selectedIndex;
                }
                return { codeName: f.codeName, value: fieldState?.value };
            });
        return { fields, selectedOptions };
    }

    const fields = Object.keys(newFieldsState)
        .filter((codeName) => {
            const val = newFieldsState[codeName]?.value;
            return typeof val === "string" ? val.trim() : val;
        })
        .map((codeName) => {
            const field = newFieldsState[codeName];
            const useLabels =
                LEAD_CAPTURE_ALLOWED_FIELDS.includes(field.fieldType ?? "") &&
                opts.leadCaptureForm;
            // Coerce to string before splitting — field.value may be a number/boolean
            // for non-text inputs (e.g. radio, slider).
            const selected =
                typeof field.value === "string"
                    ? field.value.split(",")
                    : [String(field.value)];

            const selectedIndex = parseSelectedOptionIndex(
                field.optionSelectedIndex,
            );
            if (selectedIndex !== undefined) {
                selectedOptions[codeName] = selectedIndex;
            }

            return {
                codeName,
                value: useLabels
                    ? (field.defaultValues?.options
                          .filter((el) => selected.includes(el.value))
                          .map((el) => el.label)
                          .join(",") as string)
                    : field.value,
            };
        });

    return { fields, selectedOptions };
};

/**
 * Fills skipped questions with their default answers from field meta.
 * For any field not in filledFields that has a skippableDefaultAnswer in meta,
 * adds that field with the default value to the filledFields array.
 */
export const fillDefaultAnswersForSkippedQuestions = (
    filledFields: FieldWithValue[],
    fields: Field[],
    selectedOptions: Record<string, number> = {},
): FilledDefaultsResult => {
    const filledCodeNames = new Set(filledFields.map((f) => f.codeName));

    const getSkippableDefaultValue = (field: Field): string | undefined => {
        const meta = field.meta as unknown;
        let skippableDefaultAnswer: unknown;

        if (Array.isArray(meta)) {
            skippableDefaultAnswer = meta.find(
                (item): item is { key: string; value: unknown } =>
                    typeof item === "object" &&
                    item !== null &&
                    "key" in item &&
                    (item as { key: unknown }).key ===
                        "skippableDefaultAnswer" &&
                    "value" in item,
            )?.value;
        } else if (meta && typeof meta === "object") {
            skippableDefaultAnswer = (meta as Record<string, unknown>)
                .skippableDefaultAnswer;
        }

        if (
            skippableDefaultAnswer &&
            typeof skippableDefaultAnswer === "object" &&
            "value" in skippableDefaultAnswer &&
            typeof (skippableDefaultAnswer as { value: unknown }).value ===
                "string"
        ) {
            return (skippableDefaultAnswer as { value: string }).value;
        }

        if (typeof skippableDefaultAnswer === "string") {
            try {
                const parsed = JSON.parse(skippableDefaultAnswer) as {
                    value?: unknown;
                };
                if (typeof parsed?.value === "string") {
                    return parsed.value;
                }
            } catch {
                return skippableDefaultAnswer;
            }
        }

        return undefined;
    };

    // Add missing fields that define a skippable default answer and keep only
    // entries where a valid default value could be extracted from meta.
    const defaultAnswersToAdd = fields
        .filter((field) => !filledCodeNames.has(field.codeName))
        .map((field) => {
            const value = getSkippableDefaultValue(field);
            return value
                ? {
                      codeName: field.codeName,
                      value,
                  }
                : null;
        })
        .filter((field): field is FieldWithValue => field !== null);

    return {
        fields: [...filledFields, ...defaultAnswersToAdd],
        autoFilledFieldCodeNames: defaultAnswersToAdd.map(
            (field) => field.codeName,
        ),
        selectedOptions,
    };
};

/**
 * Builds the body for `submitContactUsForm` (lead-capture / "Partner With Us" flow).
 */
export const buildContactUsPayload = (
    fields: FieldWithValue[],
    opts: { googleReCaptcha: string | null; categoryName: string | undefined },
) => {
    const find = (codeName: string) =>
        fields.find((el) => el.codeName === codeName)?.value;

    return {
        email: find("email"),
        firstName: find("firstName"),
        googleReCaptcha: opts.googleReCaptcha,
        lastName: find("lastName"),
        phone: find("phoneNumber"),
        type: "partnerWithUs",
        customFields: [
            { key: "category", value: opts.categoryName },
            ...fields
                .filter((el) => !CONTACT_US_MAIN_FIELDS.includes(el.codeName))
                .map((el) => ({ key: el.codeName, value: el.value })),
        ],
    };
};

/** Builds the redirect URL used when `goToListPage` is set on submit. */
export const buildOffersListRedirectUrl = (
    fields: FieldWithValue[],
    opts: {
        query: Record<string, string | string[] | undefined>;
        categorySlugAlias: string | undefined;
    },
): string => {
    const filters = fields.reduce<{ [key: string]: string }>((acc, item) => {
        if (item.codeName === "topClinicalTrialsAgeField") {
            acc["age"] =
                item.value === "topClinicalTrialsAgeField_18+"
                    ? "18-100"
                    : item.value.split("_")[1];
            return acc;
        }
        if (item.codeName === "topClinicalTrialsGenderField") {
            acc["gender"] = item.value.split("_")[1];
            return acc;
        }
        acc[item.codeName] = item.value;
        return acc;
    }, {});

    const zip =
        (fields.find((el) => el.codeName === "zipCode")?.value as string) ?? "";
    const formattedZip = zip.length === 4 ? `0${zip}` : zip;

    return (
        `${window.location.origin}/offers?zc=${formattedZip}` +
        `&filters=${base64EncodeUnicode(JSON.stringify(filters))}` +
        `&referrer=${opts.categorySlugAlias ?? ""}` +
        `&${gettingQueriesAsString(opts.query, "inline", ["category"])}`
    );
};

/** Parses the `query.x` redirect-config payload (rejected-lead cross-domain redirect). */
export const parseRedirectConfig = (
    rawX: string | string[] | undefined,
): { redirectedLeadId?: string; excludedBuyers?: string } => {
    if (!rawX) return {};
    try {
        // @ts-ignore
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-assignment
        return JSON.parse(base64DecodeUnicode(rawX));
    } catch {
        return {};
    }
};

/** Builds the `meta` block shared across all `submitLeadForm` calls. */
export const buildSharedMeta = (input: {
    selectedCountryCode: string | null | undefined;
    skippedValidationFields: string[] | undefined;
    autoFilledFieldCodeNames?: string[];
    selectedOptions?: Record<string, number>;
    timeOnSiteSeconds: number;
    consent: boolean | undefined;
    hasDomainConsent: boolean | undefined;
    referredByDomain: string | string[] | undefined;
    redirectedLeadId?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
}): Record<string, any> => ({
    countryCode: input.selectedCountryCode || "US",
    skippedValidationFields: input.skippedValidationFields?.length
        ? input.skippedValidationFields.join(",")
        : "",
    autoFilledFieldCodeNames: input.autoFilledFieldCodeNames?.length
        ? input.autoFilledFieldCodeNames.join(",")
        : "",
    ...(input.selectedOptions &&
        Object.keys(input.selectedOptions).length > 0 && {
            selectedOption: input.selectedOptions,
        }),
    timeOnSite: input.timeOnSiteSeconds,
    lander: localStorage.getItem("lander") ?? "",
    consent: input.consent,
    hasDomainConsent: input.hasDomainConsent ?? undefined,
    referredByDomain: input.referredByDomain,
    ...(input.redirectedLeadId && {
        redirectedLeadId: input.redirectedLeadId,
    }),
});

/**
 * Builds the second-service `meta` extension. Only relevant when the
 * domain has second-service enabled AND the current call is for the
 * second form — links it back to the first-service lead via the
 * `mainId` stored in localStorage.
 */
export const buildSecondServiceMeta = (input: {
    isSecondService: boolean | undefined;
    secondForm: boolean | undefined;
    categoryName: string | undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
}): Record<string, any> => {
    if (!input.isSecondService || !input.secondForm) return {};
    const mainId = localStorage.getItem("mainId");
    return {
        isSecondService: true,
        ...(mainId ? { parent: mainId } : {}),
        startingService: input.categoryName || "",
    };
};
